/* ============================================================
   "We filled these in from your last run."

   Rule two of the profile: a figure that appears in a form without the
   user putting it there is an assumption, and an unnoticed assumption
   is how someone tenders against a payload they changed last month.

   So every prefill is declared — what was filled, what it says, and how
   old it is — with one control to empty those fields again. The age is
   the part that matters most: "corridor, payload · 6 weeks ago" invites
   a second look in a way that a silently populated box never will.
   ============================================================ */

import { html, raw } from '../shared/html.js';
import { icon } from '../shared/icons.js';
import { describeAge, clearProfile } from '../shared/profile.js';

/**
 * @param {Array}       applied  the return of prefillForm()
 * @param {HTMLElement} root     the form the fields live in
 * @param {Function}    onClear  called after the fields are emptied
 * @returns {HTMLElement|null} null when nothing was prefilled
 */
export function PrefillNotice(applied, root, onClear) {
  if (!applied || applied.length === 0) return null;

  // The oldest field sets the tone. If the corridor is six weeks old and
  // the payload is from this morning, the thing worth checking is the
  // corridor, so that is the age we lead with.
  const oldest = applied.reduce((max, a) => Math.max(max, a.ageDays), 0);
  const names = applied.map((a) => a.label);
  const list =
    names.length === 1
      ? names[0]
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  const node = document.createElement('div');
  node.className = 'notice notice-info no-print';
  node.style.marginBottom = 'var(--ca-space-4)';
  node.innerHTML = html`
    <span style="flex-shrink:0;margin-top:2px;">${raw(icon('refresh', 15))}</span>
    <div style="font-size: var(--ca-text-xs); flex:1;">
      <strong style="font-weight: var(--ca-weight-medium);">Filled in from your earlier work</strong>
      — ${list}, last entered ${describeAge(oldest)}. Change anything that has moved.
      <button
        type="button"
        id="prefill-clear"
        class="btn btn-ghost btn-sm"
        style="margin-left: var(--ca-space-2); padding-inline: var(--ca-space-2);"
      >
        Start blank
      </button>
    </div>
  `;

  node.querySelector('#prefill-clear').addEventListener('click', () => {
    // Clear the record, not just this form. Someone asking for a blank
    // sheet means it, and leaving the profile in place so the next tool
    // repopulates would read as the app ignoring them.
    clearProfile();
    for (const field of applied) {
      const el = root?.querySelector(`#${field.elementId}`);
      if (el) el.value = '';
    }
    node.remove();
    if (typeof onClear === 'function') onClear();
  });

  return node;
}
